import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "@/lib/site";
import { getToken, setToken, bootstrapToken } from "@/lib/authToken";

const AuthCtx = createContext(null);

// Restore Bearer header immediately on module load — before any axios call fires.
bootstrapToken();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") { setLoading(false); return; }
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Only probe /auth/me when on an /admin route — public pages don't need it.
    if (!window.location.pathname.startsWith("/admin")) {
      setLoading(false);
      return;
    }
    // No stored token → not logged in. Skip the /me round trip.
    if (!getToken()) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("[auth] logout request failed:", err);
    }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, setUser, checkAuth, logout }),
    [user, loading, checkAuth, logout]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
